import { useEffect } from 'react';
import './AuraSphereAnimation.css';

/**
 * PROFESSIONAL AURA SPHERE ANIMATION
 * Fighting-type special move - fires a sphere of aura energy (never misses)
 * 
 * Key Features:
 * - Fighting-type special attack (80 base power, never misses)
 * - Lucario signature move with blue aura energy orb
 * - Professional gaming quality with flowing aura effects
 */
export default function AuraSphereAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;

    const elements = [];

    // PHASE 1: Aura Gathering (0-500ms)
    playerEl.classList.add('aura-sphere-user-charge');

    const auraGlow = document.createElement('div');
    auraGlow.className = 'aura-sphere-glow';
    auraGlow.style.left = `${playerX - 70}px`;
    auraGlow.style.top = `${playerY - 70}px`;
    container.appendChild(auraGlow);
    elements.push(auraGlow);

    // Aura sphere forming between hands
    const formingSphere = document.createElement('div');
    formingSphere.className = 'aura-sphere-forming';
    formingSphere.style.left = `${playerX - 18}px`;
    formingSphere.style.top = `${playerY - 18}px`;
    container.appendChild(formingSphere);
    elements.push(formingSphere);

    // Aura wisps gathering inward
    for (let i = 0; i < 10; i++) {
      const wisp = document.createElement('div');
      wisp.className = 'aura-sphere-wisp';
      const angle = (i / 10) * 360;
      const radius = 60;
      wisp.style.left = `${playerX + Math.cos(angle * Math.PI / 180) * radius - 4}px`;
      wisp.style.top = `${playerY + Math.sin(angle * Math.PI / 180) * radius - 4}px`;
      wisp.style.animationDelay = `${i * 40}ms`;
      wisp.style.setProperty('--target-x', `${playerX - (playerX + Math.cos(angle * Math.PI / 180) * radius)}px`);
      wisp.style.setProperty('--target-y', `${playerY - (playerY + Math.sin(angle * Math.PI / 180) * radius)}px`);
      container.appendChild(wisp);
      elements.push(wisp);
    }

    // PHASE 2: Aura Sphere Launch (500-1000ms)
    const phase2Timer = setTimeout(() => {
      if (formingSphere.parentNode) formingSphere.remove();

      const sphere = document.createElement('div');
      sphere.className = 'aura-sphere-projectile';
      sphere.style.left = `${playerX}px`;
      sphere.style.top = `${playerY}px`;
      sphere.style.setProperty('--dx', `${dx}px`);
      sphere.style.setProperty('--dy', `${dy}px`);
      container.appendChild(sphere);
      elements.push(sphere);

      // Aura trail
      for (let i = 0; i < 14; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'aura-sphere-trail';
          const progress = i / 14;
          trail.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 18 - 6}px`;
          trail.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 18 - 6}px`;
          container.appendChild(trail);
          elements.push(trail);
        }, i * 30);
        elements.push({ timer: trailTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Aura Impact (1000-1500ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('aura-sphere-opponent-hit');

      const explosion = document.createElement('div');
      explosion.className = 'aura-sphere-explosion';
      explosion.style.left = `${opponentX - 100}px`;
      explosion.style.top = `${opponentY - 100}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      for (let i = 0; i < 3; i++) {
        const ringTimer = setTimeout(() => {
          const ring = document.createElement('div');
          ring.className = 'aura-sphere-ring';
          ring.style.left = `${opponentX - 60}px`;
          ring.style.top = `${opponentY - 60}px`;
          container.appendChild(ring);
          elements.push(ring);
        }, i * 100);
        elements.push({ timer: ringTimer, type: 'timer' });
      }

      for (let i = 0; i < 16; i++) {
        const burstAngle = (i / 16) * 360;
        const burstDist = 50 + Math.random() * 40;
        const burst = document.createElement('div');
        burst.className = 'aura-sphere-burst';
        burst.style.left = `${opponentX - 4}px`;
        burst.style.top = `${opponentY - 4}px`;
        burst.style.setProperty('--scatter-x', `${Math.cos(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.setProperty('--scatter-y', `${Math.sin(burstAngle * Math.PI / 180) * burstDist}px`);
        burst.style.animationDelay = `${i * 15}ms`;
        container.appendChild(burst);
        elements.push(burst);
      }
    }, 1000);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Settle (1500-1800ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('aura-sphere-user-charge');
    }, 1500);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1800ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('aura-sphere-user-charge');
      opponentEl.classList.remove('aura-sphere-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 1800);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('aura-sphere-user-charge');
      opponentEl.classList.remove('aura-sphere-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

AuraSphereAnimation.moveType = 'fighting';
